const SYRINGES_ML = [0.3, 0.5, 1, 3];
const U100_UNITS_PER_ML = 100;
const EPSILON = 1e-10;

export function measureNumber(raw) {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(trimmed)) return null;

  const value = Number(trimmed);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function fromDisplay(value, unit) {
  if (value === null) return null;
  if (unit === "mcg") return value / 1000;
  if (unit === "units") return value / U100_UNITS_PER_ML;
  return value;
}

export function toDisplay(value, unit) {
  if (value === null) return null;
  if (unit === "mcg") return value * 1000;
  if (unit === "units") return value * U100_UNITS_PER_ML;
  return value;
}

export function calculateMeasure(state) {
  const quantitiesReady =
    state.compounds.length > 0 && state.compounds.every((compound) => positive(compound.quantity));
  const volumeReady = positive(state.volumeMl);
  const supplyReady = quantitiesReady && (state.entryMode === "strength" || volumeReady);

  const concentrations = supplyReady
    ? state.compounds.map((compound) =>
        state.entryMode === "strength" ? compound.quantity : compound.quantity / state.volumeMl
      )
    : [];
  const concentration = supplyReady ? concentrations.reduce((sum, value) => sum + value, 0) : null;
  const totalQuantity = quantitiesReady ? state.compounds.reduce((sum, compound) => sum + compound.quantity, 0) : null;
  const totalInVial =
    state.entryMode === "amount"
      ? totalQuantity
      : volumeReady && totalQuantity !== null
        ? totalQuantity * state.volumeMl
        : null;

  let volumeMl = null;
  if (supplyReady && positive(state.target.value)) {
    if (state.target.kind === "volume") {
      volumeMl = state.target.value;
    } else if (state.target.kind === "total" && concentration !== null) {
      volumeMl = state.target.value / concentration;
    } else if (state.target.kind === "component") {
      const index = state.compounds.findIndex((compound) => compound.id === state.target.id);
      if (index >= 0) volumeMl = state.target.value / concentrations[index];
    }
  }

  const doses = positive(volumeMl) ? concentrations.map((value) => value * volumeMl) : [];
  const ready = positive(volumeMl) && doses.every(positive);
  if (!ready) volumeMl = null;
  const exceedsSyringe = volumeMl !== null && volumeMl > state.syringeMl + EPSILON;

  return {
    ready,
    supplyReady,
    concentration,
    concentrations: supplyReady ? concentrations : [],
    totalInVial: totalInVial !== null && Number.isFinite(totalInVial) ? totalInVial : null,
    volumeMl,
    doses: ready ? doses : [],
    totalDose: ready ? doses.reduce((sum, value) => sum + value, 0) : null,
    fullDoses: ready && volumeReady ? Math.floor(state.volumeMl / volumeMl + EPSILON) : null,
    exceedsSyringe,
    exceedsVial: volumeMl !== null && volumeReady && volumeMl > state.volumeMl + EPSILON,
    largerSyringe: exceedsSyringe ? (SYRINGES_ML.find((size) => size >= volumeMl - EPSILON) ?? null) : null,
  };
}

// Mirrors Reconstitution::Units.computed_text so the browser and the server write a
// computed field the same way.
export function formatComputed(value, { grouping = false } = {}) {
  if (value === null || !Number.isFinite(value)) return "";
  const digits = value >= 0.0001 ? { maximumFractionDigits: 4 } : { maximumSignificantDigits: 12 };
  return value.toLocaleString("en-US", { ...digits, useGrouping: grouping });
}

export function formatInput(value) {
  if (value === null || !Number.isFinite(value)) return "";
  return value.toLocaleString("en-US", { maximumSignificantDigits: 12, useGrouping: false });
}

function positive(value) {
  return value !== null && Number.isFinite(value) && value > 0;
}
